import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Bell, Loader2, Globe, UserCircle2, AlarmClock, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ICONS = { Globe, UserCircle2, AlarmClock, MessageSquare };
const COLOR_CLASSES = {
  emerald: { border: "border-emerald-200", bg: "bg-emerald-50/60", text: "text-emerald-700", icon: "text-emerald-600" },
  indigo: { border: "border-indigo-200", bg: "bg-indigo-50/60", text: "text-indigo-700", icon: "text-indigo-600" },
  amber: { border: "border-amber-200", bg: "bg-amber-50/60", text: "text-amber-700", icon: "text-amber-600" },
  blue: { border: "border-blue-200", bg: "bg-blue-50/60", text: "text-blue-700", icon: "text-blue-600" },
};

export default function MyNotificationPrefs() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/notifications");
        setChannels(data?.channels || []);
        setPrefs(data?.prefs || {});
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore caricamento preferenze");
      } finally { setLoading(false); }
    })();
  }, []);

  const toggle = async (field, value) => {
    setPrefs((p) => ({ ...p, [field]: value }));
    setSaving((s) => ({ ...s, [field]: true }));
    try {
      await api.patch("/me/notifications", { [field]: value });
      toast.success(`Preferenza ${value ? "attivata" : "disattivata"}`);
    } catch (e) {
      setPrefs((p) => ({ ...p, [field]: !value }));
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setSaving((s) => { const c = { ...s }; delete c[field]; return c; });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="my-notifications-page">
      <div>
        <h1 className="font-display text-2xl font-bold flex items-center gap-2">
          <Bell className="h-6 w-6 text-blue-600" />
          Le mie notifiche email
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Ciao <strong>{user?.name}</strong>, qui puoi decidere quali email automatiche vuoi ricevere dall&apos;agenzia.
          Le modifiche vengono salvate al volo. Disattivare un canale non blocca gli altri colleghi: tu semplicemente non riceverai quel tipo di email.
        </p>
      </div>

      <div className="space-y-3">
        {channels.map((c) => {
          const Icon = ICONS[c.icon] || Bell;
          const col = COLOR_CLASSES[c.color] || COLOR_CLASSES.blue;
          const field = `notify_${c.key}`;
          const enabled = !!prefs[field];
          return (
            <Card key={c.key} className={`${col.border} ${col.bg} transition-all ${enabled ? "" : "opacity-70"}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <Icon className={`h-7 w-7 flex-shrink-0 ${col.icon}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${col.text}`}>{c.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {c.key === "public_quotes" && "Email quando arriva una richiesta dal sito pubblico /richiedi-preventivo"}
                    {c.key === "portal_quotes" && "Email quando un cliente loggato chiede un nuovo preventivo dal portale"}
                    {c.key === "expiring_policies" && "Riepilogo email con polizze in scadenza (cron giornaliero)"}
                    {c.key === "client_messages" && "Email quando un cliente risponde via chat su un preventivo"}
                  </div>
                </div>
                <Switch
                  data-testid={`pref-${c.key}`}
                  checked={enabled}
                  disabled={!!saving[field]}
                  onCheckedChange={(v) => toggle(field, v)}
                />
              </CardContent>
            </Card>
          );
        })}
      </div>

      <p className="text-xs text-slate-400">
        Vuoi gestire le preferenze di un altro collega? Solo gli amministratori possono farlo da <code className="bg-slate-100 px-1 rounded">Notifiche preventivi</code> nel menu Gestione Agenzia.
      </p>
    </div>
  );
}
